import { yupResolver } from '@hookform/resolvers/yup';
import { Cancel, CheckCircle, Edit, MoreHoriz } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { fDate } from '@norma/shared/helpers/formatTime';
import { User } from '@norma/shared/types/auth.type';
import { Organization } from '@norma/shared/types/organization.type';
import { UpdateSubscriptionDto } from '@norma/shared/types/subscription.type';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { theme } from 'src/constants/theme';
import {
  canCancelSubscriptionAsAdmin,
  isAutomaticSubscriptionAvailableForOrganization,
} from 'src/helpers/subscription';
import { dateString } from 'src/helpers/yup';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import {
  selectTemplateOrganization,
  updateTemplateOrganization,
} from 'src/slices/organization';
import { organizationSelector } from 'src/store/selectors';
import * as yup from 'yup';
import { CheckboxFormField } from '../forms/CheckboxFormField';
import { DatePickerForm } from '../forms/DatePickerForm';
import { TextFieldForm } from '../forms/TextFieldForm';
import { IMaskNumberProps, MaskNumber } from '../masks/MaskNumber';
import { OrganizationArchiveDialog } from './OrganizationArchiveDialog';
import { OrganizationMemberDialog } from './OrganizationMemberDialog';

interface Props {
  organization: Organization;
  updateSubscription: (data: UpdateSubscriptionDto) => void;
  cancelSubscription: (subscriptionId: string) => void;
  users: User[];
  dispatch: ReturnType<typeof useAppDispatch>;
}

const OrganizationCard: React.FC<Props> = ({
  organization,
  updateSubscription,
  cancelSubscription,
  users,
  dispatch,
}) => {
  const { t } = useTranslation();
  const [editSubscription, setEditSubscription] = useState<boolean>(false);
  const [organizationMemberDialogOpen, setOrganizationMemberDialogOpen] =
    useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [editExport, setEditExport] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingTemplates, setPendingTemplates] = useState<{
    [key in 'cover' | 'body']?: string;
  }>({});
  const coverInputRef = useRef<HTMLInputElement>(null);
  const bodyInputRef = useRef<HTMLInputElement>(null);

  const getTemplatesByType = (type: 'cover' | 'body') =>
    org.templates?.filter((template) => template.type === type) || [];

  const onTemplateUpload = async (file: File, type: 'cover' | 'body') => {
    if (!org._id) return;
    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('filename', file.name);

    await dispatch(
      updateTemplateOrganization({
        organizationId: org._id,
        data: { formData },
      }),
    );

    setIsUploading(false);
  };

  const handleSelectTemplate = async (
    type: 'cover' | 'body',
    storageName: string,
  ) => {
    if (org._id) {
      await dispatch(
        selectTemplateOrganization({
          organizationId: org._id,
          storageName,
          type,
        }),
      );
    }
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'cover' | 'body',
  ) => {
    if (e.target.files && e.target.files[0]) {
      await onTemplateUpload(e.target.files[0], type);
    }
  };

  const handlePendingTemplateChange = (
    type: 'cover' | 'body',
    storageName: string,
  ) => {
    setPendingTemplates((prev) => ({
      ...prev,
      [type]: storageName,
    }));
  };

  const handleSaveTemplates = async () => {
    for (const type of ['cover', 'body'] as const) {
      const selected = getTemplatesByType(type).find(
        (tpl) => tpl.isSelected,
      )?.storageName;
      const pending = pendingTemplates[type];
      if (pending !== selected) {
        await handleSelectTemplate(type, pending ?? '');
      }
    }
    setEditExport(false);
  };

  const handleCancelTemplates = () => {
    setEditExport(false);
    setPendingTemplates({});
  };

  const organizationFromStore = useAppSelector((state) =>
    organizationSelector.selectById(state, organization._id),
  );
  const org = organizationFromStore || organization;

  const { control, handleSubmit, reset, setValue } =
    useForm<UpdateSubscriptionDto>({
      defaultValues: {
        isPaying: org.subscription?.isPaying,
        isFreeSubscription: org.subscription?.isFreeSubscription,
        isFreeTrial: org.subscription?.isFreeTrial,
        freeTrialEndDate: org.subscription?.freeTrialEndDate,
        maxNumberOfUsers: org.subscription?.maxNumberOfUsers,
        subscriptionPeriodStartDate:
          org.subscription?.subscriptionPeriodStartDate,
        subscriptionPeriodEndDate: org.subscription?.subscriptionPeriodEndDate,
        notes: org.subscription?.notes,
        paymentLink: org.subscription?.paymentLink,
      },
      resolver: yupResolver(
        yup.object<UpdateSubscriptionDto>({
          isPaying: yup.boolean().optional(),
          isFreeSubscription: yup.boolean().optional(),
          isFreeTrial: yup.boolean().optional(),
          freeTrialEndDate: dateString().when(
            ['isFreeTrial'],
            ([isFreeTrial], schema) => {
              if (isFreeTrial) {
                return schema.required(t('forms.rules.required'));
              }
              return schema.optional();
            },
          ),
          maxNumberOfUsers: yup
            .number()
            .optional()
            .min(0, 'Ce champ doit être supérieure à 0')
            .max(10000),
          subscriptionPeriodStartDate: dateString().when(
            ['isPaying'],
            (isPaying, schema) => {
              const base = isPaying
                ? schema.required(t('forms.rules.required'))
                : schema.optional();

              return org.subscription?.subscriptionPeriodEndDate
                ? base.maxDate(
                    undefined,
                    t('pages.Admin.organization.validationSchema.maxDate'),
                    org.subscription.subscriptionPeriodEndDate,
                  )
                : base;
            },
          ),
          subscriptionPeriodEndDate: dateString().when(
            ['isPaying'],
            (isPaying, schema) => {
              const base = isPaying
                ? schema.required(t('forms.rules.required'))
                : schema.optional();

              return org.subscription?.subscriptionPeriodStartDate
                ? base.minDate(
                    undefined,
                    t('pages.Admin.organization.validationSchema.minDate'),
                    org.subscription.subscriptionPeriodStartDate,
                  )
                : base;
            },
          ),
          notes: yup.string().optional(),
          paymentLink: yup.string().url(t('forms.rules.validUrl')).optional(),
        }),
      ),
    });

  const maxNumberOfUsers = control._formValues.maxNumberOfUsers;
  const [maxNumberAuthorized, setMaxNumberAuthorized] = useState<number>(
    (maxNumberOfUsers ?? 0) + 1,
  );

  useEffect(() => {
    setMaxNumberAuthorized((maxNumberOfUsers ?? 0) + 1);
  }, [maxNumberOfUsers]);

  useEffect(() => {
    if (editExport) {
      setPendingTemplates({
        cover:
          getTemplatesByType('cover').find((tpl) => tpl.isSelected)
            ?.storageName || '',
        body:
          getTemplatesByType('body').find((tpl) => tpl.isSelected)
            ?.storageName || '',
      });
    }
  }, [editExport, org]);

  useEffect(() => {
    reset({
      isPaying: org.subscription?.isPaying,
      isFreeSubscription: org.subscription?.isFreeSubscription,
      isFreeTrial: org.subscription?.isFreeTrial,
      freeTrialEndDate: org.subscription?.freeTrialEndDate,
      maxNumberOfUsers: org.subscription?.maxNumberOfUsers,
      subscriptionPeriodStartDate:
        org.subscription?.subscriptionPeriodStartDate,
      subscriptionPeriodEndDate: org.subscription?.subscriptionPeriodEndDate,
      notes: org.subscription?.notes,
      paymentLink: org.subscription?.paymentLink,
    });
  }, [org, reset]);

  const subscriptionResetEdit = async () => {
    setEditSubscription(false);
  };

  const onSubmitSubscription = async (data: UpdateSubscriptionDto) => {
    updateSubscription({
      ...data,
      ...(!org.subscription?.isFreeTrial && data.isFreeTrial
        ? {
            freeTrialStartDate: new Date(),
          }
        : {}),
      subscriptionPeriods: [
        ...(org.subscription?.subscriptionPeriods || []),
        {
          startDate: data.subscriptionPeriodStartDate || new Date(),
          endDate: data.subscriptionPeriodEndDate || new Date(),
        },
      ],
    });
    subscriptionResetEdit();
  };

  const organizationRegisterDate = new Date(org.createdAt ?? Date.now());

  const isPaying = control._formValues.isPaying;
  const isFreeSubscription = control._formValues.isFreeSubscription;
  const isAutomatic = org.subscription?.isAutomaticSubscriptionPaymentActive;

  const displayEditSubscription = () => {
    const automaticSubscriptionAvailable =
      isAutomaticSubscriptionAvailableForOrganization(org);

    return (
      <form
        style={{ marginTop: theme.spacing(3) }}
        onSubmit={handleSubmit(onSubmitSubscription, console.log)}
      >
        <TextFieldForm
          label={t('subscription.fields.notes')}
          name="notes"
          control={control}
          style={{ width: '100%', marginBottom: 5 }}
        />
        <Stack marginBottom={2}>
          {!(automaticSubscriptionAvailable && isAutomatic) &&
            !isPaying &&
            !isFreeSubscription && (
              <Stack
                marginTop={1}
                marginBottom={0.5}
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
                sx={{ width: '100%' }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={'space-between'}
                  sx={{ width: '40%' }}
                >
                  <Typography variant="body1">
                    {t('pages.Admin.subscription.freeTrial', {
                      startDate: !!org.subscription?.freeTrialStartDate
                        ? fDate(org.subscription?.freeTrialStartDate)
                        : '',
                      context: !!org.subscription?.freeTrialStartDate
                        ? 'startDate'
                        : undefined,
                    })}
                  </Typography>
                  <CheckboxFormField name="isFreeTrial" control={control} />
                </Stack>
                <DatePickerForm
                  name="freeTrialEndDate"
                  control={control}
                  label={t('pages.Admin.subscription.freeTrialEndDate')}
                  sx={{ width: 300 }}
                />
              </Stack>
            )}
          {Boolean(!isAutomatic && !isPaying) && (
            <Stack
              marginBottom={1}
              direction="row"
              alignItems="center"
              justifyContent={'space-between'}
              sx={{ width: '40%' }}
            >
              <Typography variant="body1">
                {t('pages.Admin.subscription.free')}
              </Typography>
              <CheckboxFormField name="isFreeSubscription" control={control} />
            </Stack>
          )}
          {Boolean(!isAutomatic) && (
            <Stack
              marginBottom={1}
              direction="row"
              alignItems="center"
              justifyContent={'space-between'}
              sx={{ width: '40%' }}
            >
              <Typography variant="body1">
                {t('pages.Admin.subscription.manual')}
              </Typography>
              <CheckboxFormField name="isPaying" control={control} />
            </Stack>
          )}
        </Stack>

        <TextFieldForm
          label={t('subscription.fields.paymentLink')}
          name="paymentLink"
          control={control}
          style={{ width: '100%' }}
        />
        {automaticSubscriptionAvailable ? (
          <Stack marginTop={2} marginBottom={0.5} spacing={2}>
            <Typography variant="body1" fontWeight="bold">
              {t('pages.Admin.subscription.automatic.title')}
            </Typography>
            {canCancelSubscriptionAsAdmin(org.subscription) ? (
              <Button
                variant="contained"
                onClick={() => {
                  if (org.subscription?._id) {
                    cancelSubscription(org.subscription?._id);
                  }
                }}
                sx={{ alignSelf: 'start' }}
              >
                {t('pages.Admin.subscription.automatic.cancel')}
              </Button>
            ) : null}
            {org.subscription?.isFreeTrial && isAutomatic ? (
              <>
                <Typography variant="body1">
                  {t('pages.Admin.subscription.automatic.freeTrial', {
                    startDate: !!org.subscription?.freeTrialStartDate
                      ? fDate(org.subscription?.freeTrialStartDate)
                      : '',
                    endDate: !!org.subscription?.freeTrialEndDate
                      ? fDate(org.subscription?.freeTrialEndDate)
                      : '',
                  })}
                </Typography>
              </>
            ) : (
              <>
                {!!org.subscription?.subscriptionPeriodStartDate ? (
                  <Typography variant="body1">
                    {t('pages.Admin.subscription.automatic.lastPayment', {
                      lastPaymentDate: fDate(
                        org.subscription?.subscriptionPeriodStartDate,
                      ),
                    })}
                  </Typography>
                ) : null}
                {!!org.subscription?.subscriptionPeriodEndDate ? (
                  <Typography variant="body1">
                    {org.subscription?.cancelAtPeriodEnd
                      ? t(
                          'pages.Admin.subscription.automatic.cancelAtPeriodEnd',
                          {
                            endDate: fDate(
                              org.subscription?.subscriptionPeriodEndDate,
                            ),
                          },
                        )
                      : t('pages.Admin.subscription.automatic.nextPayment', {
                          nextPaymentDate: fDate(
                            org.subscription?.subscriptionPeriodEndDate,
                          ),
                        })}
                  </Typography>
                ) : null}
              </>
            )}
          </Stack>
        ) : null}
        {!isAutomatic && (
          <TextFieldForm
            label={t('subscription.fields.maxNumberAuthorized')}
            name="maxNumberOfUsers"
            control={control}
            value={maxNumberAuthorized}
            onChange={(e: any) => {
              const value = parseInt(e.target.value, 10);
              setMaxNumberAuthorized(value);
              setValue('maxNumberOfUsers', value - 1);
            }}
            InputProps={{
              inputComponent: MaskNumber as any,
              inputProps: {
                numberMask: { scale: 0 },
              } as IMaskNumberProps,
            }}
            sx={(theme) => ({
              marginTop: theme.spacing(2),
              marginBottom: theme.spacing(2),
            })}
          />
        )}
        {!isFreeSubscription && (
          <Stack spacing={1} marginTop={1} direction="row" alignItems="center">
            <DatePickerForm
              name="subscriptionPeriodStartDate"
              control={control}
              label={t('pages.Admin.organization.subscriptionPeriodStartDate')}
            />
            <DatePickerForm
              name="subscriptionPeriodEndDate"
              control={control}
              label={t('pages.Admin.organization.subscriptionPeriodEndDate')}
            />
          </Stack>
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ margin: theme.spacing(2, 0, 0.5, 0) }}
          spacing={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={subscriptionResetEdit}
            fullWidth
          >
            {t('common.cancel')}
          </Button>
          <Button variant="contained" type="submit" color="primary" fullWidth>
            {t('common.save')}
          </Button>
        </Stack>
      </form>
    );
  };

  return (
    <>
      <Card>
        <CardHeader
          title={<Typography variant="h6">{org.name}</Typography>}
          sx={{
            backgroundColor: theme.palette.grey[200],
            padding: theme.spacing(2),
          }}
        />
        <CardContent
          sx={{
            maxHeight: '800px',
            overflowY: 'auto',
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                {t('pages.Admin.company')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {org.type
                  ? t(`organization.fields.type.options.${org.type}`)
                  : ''}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center">
                <Typography variant="h5">
                  {t('pages.Admin.subscription.title')}
                </Typography>
                <IconButton
                  style={{ marginLeft: theme.spacing(3) }}
                  onClick={() => setEditSubscription(!editSubscription)}
                >
                  <Edit />
                </IconButton>
              </Stack>
              {editSubscription ? (
                <Box sx={{ marginTop: theme.spacing(2) }}>
                  {displayEditSubscription()}
                </Box>
              ) : (
                <Stack direction="row" alignItems="center">
                  <Box>
                    {org.subscription?.notes && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                        sx={{ marginBottom: 2 }}
                      >
                        {t('subscription.notes', {
                          notes: org.subscription.notes,
                        })}
                      </Typography>
                    )}

                    <Typography
                      variant="body2"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ marginBottom: 2 }}
                    >
                      {org.subscription?.isFreeSubscription
                        ? t('pages.Admin.subscription.free')
                        : org.subscription?.isFreeTrial
                          ? t('pages.Admin.subscription.freeTrial', {
                              startDate:
                                !!org.subscription?.freeTrialStartDate &&
                                fDate(org.subscription?.freeTrialStartDate),
                              endDate:
                                !!org.subscription?.freeTrialEndDate &&
                                fDate(org.subscription?.freeTrialEndDate),
                            })
                          : org.subscription?.isPaying
                            ? t('pages.Admin.subscription.manual')
                            : isAutomatic
                              ? t('pages.Admin.subscription.automatic.title')
                              : t('pages.Admin.subscription.none')}
                      {Boolean(
                        org.subscription?.isFreeSubscription ||
                          org.subscription?.isFreeTrial ||
                          org.subscription?.isPaying ||
                          isAutomatic,
                      ) ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Cancel color="error" />
                      )}
                    </Typography>
                    {isAutomatic &&
                    (org.subscription.paymentLink ||
                      org.subscription.stripeCustomerId) ? (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                        sx={{ marginBottom: 2 }}
                      >
                        <a
                          href={
                            org.subscription.stripeCustomerId
                              ? `https://dashboard.stripe.com/customers/${org.subscription.stripeCustomerId}`
                              : org.subscription.paymentLink
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: 'none',
                            color: theme.palette.primary.main,
                          }}
                        >
                          {t('subscription.fields.paymentLink')}
                        </a>
                      </Typography>
                    ) : null}
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ marginBottom: 2 }}
                    >
                      <Typography variant="body2">
                        {t('pages.Admin.subscription.members', {
                          count: org.members?.length ?? 0,
                        })}
                      </Typography>
                      {org.members && org.members.length > 0 && (
                        <IconButton
                          style={{ marginLeft: theme.spacing(3) }}
                          onClick={() => setOrganizationMemberDialogOpen(true)}
                        >
                          <MoreHoriz />
                        </IconButton>
                      )}
                    </Stack>

                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ marginBottom: 2 }}
                    >
                      {t('pages.Admin.subscription.maxNumberAuthorized', {
                        count: (org.subscription?.maxNumberOfUsers || 0) + 1,
                      })}
                    </Typography>
                    {org.subscription?.isFreeTrial && isAutomatic ? (
                      <Typography variant="body2" sx={{ marginBottom: 2 }}>
                        {t('pages.Admin.subscription.automatic.freeTrial', {
                          startDate: !!org.subscription?.freeTrialStartDate
                            ? fDate(org.subscription?.freeTrialStartDate)
                            : '',
                          endDate: !!org.subscription?.freeTrialEndDate
                            ? fDate(org.subscription?.freeTrialEndDate)
                            : '',
                        })}
                      </Typography>
                    ) : (
                      <>
                        {!!org.subscription?.subscriptionPeriodStartDate && (
                          <Typography variant="body2" sx={{ marginBottom: 1 }}>
                            {t(
                              'pages.Admin.subscription.automatic.lastPayment',
                              {
                                lastPaymentDate: fDate(
                                  org.subscription?.subscriptionPeriodStartDate,
                                ),
                              },
                            )}
                          </Typography>
                        )}
                        {!!org.subscription?.subscriptionPeriodEndDate && (
                          <Typography variant="body2" sx={{ marginBottom: 2 }}>
                            {org.subscription?.cancelAtPeriodEnd
                              ? t(
                                  'pages.Admin.subscription.automatic.cancelAtPeriodEnd',
                                  {
                                    endDate: fDate(
                                      org.subscription
                                        ?.subscriptionPeriodEndDate,
                                    ),
                                  },
                                )
                              : t(
                                  'pages.Admin.subscription.automatic.nextPayment',
                                  {
                                    nextPaymentDate: fDate(
                                      org.subscription
                                        ?.subscriptionPeriodEndDate,
                                    ),
                                  },
                                )}
                          </Typography>
                        )}
                        {!isFreeSubscription && (
                          <Stack spacing={1} sx={{ marginBottom: 2 }}>
                            <Stack direction="row" alignItems="center">
                              <Typography>
                                {t(
                                  'pages.Admin.organization.subscriptionPeriodStartDate',
                                )}
                                {org.subscription?.subscriptionPeriodStartDate
                                  ? fDate(
                                      org.subscription
                                        ?.subscriptionPeriodStartDate,
                                    )
                                  : ''}
                              </Typography>
                            </Stack>
                            <Typography>
                              {t(
                                'pages.Admin.organization.subscriptionPeriodEndDate',
                              )}
                              {org.subscription?.subscriptionPeriodEndDate
                                ? fDate(
                                    org.subscription?.subscriptionPeriodEndDate,
                                  )
                                : ''}
                            </Typography>
                          </Stack>
                        )}
                        <Stack direction="row" alignItems="center">
                          <Typography variant="body2" component="div">
                            {t('pages.Admin.organization.archive')}
                          </Typography>
                          {org.members && org.members.length > 0 && (
                            <IconButton
                              style={{ marginLeft: theme.spacing(3) }}
                              onClick={() => setArchiveDialogOpen(true)}
                            >
                              <MoreHoriz />
                            </IconButton>
                          )}
                        </Stack>
                      </>
                    )}
                  </Box>
                </Stack>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography component="div" variant="h5">
                {t('pages.Admin.organization.organizationInfo')}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setEditExport(!editExport)}
                sx={{ ml: 1 }}
              >
                <Edit />
              </IconButton>
            </Stack>
            <Stack>
              {t('pages.Admin.organization.creationDateWithColon')}
              {fDate(organizationRegisterDate)}
            </Stack>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {(['cover', 'body'] as const).map((type) => {
                const templates = getTemplatesByType(type);
                const selectedTemplate = templates.find(
                  (tpl) => tpl.isSelected,
                );

                return (
                  <Stack
                    key={type}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                  >
                    <Typography sx={{ minWidth: 140 }}>
                      {type === 'cover'
                        ? t(
                            'pages.OrganizationConfiguration.templates.coverTitle',
                          )
                        : t(
                            'pages.OrganizationConfiguration.templates.bodyTitle',
                          )}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      {editExport ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {t(
                              'pages.OrganizationConfiguration.templates.selectTemplate',
                            )}
                          </InputLabel>
                          <Select
                            fullWidth
                            value={pendingTemplates[type] ?? ''}
                            label={t(
                              'pages.OrganizationConfiguration.templates.selectTemplate',
                            )}
                            onChange={(e) =>
                              handlePendingTemplateChange(
                                type,
                                e.target.value as string,
                              )
                            }
                          >
                            <MenuItem value="">
                              {t(
                                'pages.OrganizationConfiguration.templates.noTemplate',
                              )}
                            </MenuItem>
                            {templates.map((tpl) => (
                              <MenuItem
                                key={tpl.storageName}
                                value={tpl.storageName}
                              >
                                {tpl.filename}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography>
                          {selectedTemplate?.filename || (
                            <span
                              style={{ fontStyle: 'italic', color: '#888' }}
                            >
                              {t(
                                'pages.OrganizationConfiguration.templates.noTemplate',
                              )}
                            </span>
                          )}
                        </Typography>
                      )}
                    </Box>
                    {editExport && (
                      <Button
                        variant="outlined"
                        component="label"
                        disabled={isUploading}
                        sx={{ minWidth: 40, width: 40, height: 40, p: 0 }}
                      >
                        <span style={{ fontWeight: 'bold', fontSize: 18 }}>
                          +
                        </span>
                        <input
                          ref={type === 'cover' ? coverInputRef : bodyInputRef}
                          type="file"
                          accept=".docx"
                          hidden
                          onChange={(e) => handleFileChange(e, type)}
                        />
                      </Button>
                    )}
                  </Stack>
                );
              })}
              {isUploading && (
                <Typography color="primary">
                  {t('pages.OrganizationConfiguration.templates.uploading')}
                </Typography>
              )}
              {editExport && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancelTemplates}
                    fullWidth
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveTemplates}
                    fullWidth
                  >
                    {t('common.save')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Grid>
        </CardContent>
      </Card>
      {org.members && org.members.length > 0 && (
        <OrganizationMemberDialog
          open={organizationMemberDialogOpen}
          onClose={() => setOrganizationMemberDialogOpen(false)}
          organization={org}
          members={org.members}
          users={users}
        />
      )}
      <OrganizationArchiveDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        organization={org}
      />
    </>
  );
};

export default OrganizationCard;
